import { useEffect, useMemo, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { MessageSquare, Lock, Send, Search, Check, Loader2, ArrowLeft } from 'lucide-react';
import { GlassCard, CardLabel, GradientAvatar } from './GlassCard';

export default function MessagingPage() {
  const { user, api } = useAuth();
  const plan = user?.business?.plan || 'starter';
  const isLocked = plan === 'starter';

  if (isLocked) {
    return (
      <div data-testid="messaging-page-locked" className="flex flex-col items-center justify-center py-24 text-center">
        <div className="w-16 h-16 bg-gray-100 dark:bg-white/[0.05] rounded-2xl flex items-center justify-center mb-4">
          <Lock className="w-8 h-8 text-gray-400 dark:text-slate-500" />
        </div>
        <h2 className="font-heading text-2xl font-medium text-gray-900 dark:text-white mb-2">SMS Inbox</h2>
        <p className="text-gray-500 dark:text-slate-400 text-sm max-w-md mb-6">Available on Growth and Agency plans. Upgrade to unlock the two-way SMS inbox and campaigns.</p>
        <Link to="/dashboard/settings?tab=billing" className="bg-blue-600 hover:bg-blue-500 dark:hover:bg-blue-400 text-white text-sm font-medium rounded-full px-6 py-2.5">Upgrade Plan</Link>
      </div>
    );
  }

  return <MessagingInbox api={api} />;
}

function MessagingInbox({ api }) {
  const [threads, setThreads] = useState([]);
  const [activePhone, setActivePhone] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);

  const fetchThreads = async () => {
    try {
      const { data } = await api.get('/messages/threads');
      const list = data.threads || [];
      setThreads(list);
      // Auto-select the first thread only on desktop. On mobile we want the
      // user to see the LIST first, then tap through to a conversation.
      const isDesktop = typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches;
      if (!activePhone && list.length > 0 && isDesktop) setActivePhone(list[0].phone);
    } catch (e) {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThreads();
    const id = setInterval(fetchThreads, 15000); // poll for new messages every 15s
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = useMemo(() => threads.find(t => t.phone === activePhone), [threads, activePhone]);

  // Mark thread as read when you open it
  useEffect(() => {
    if (active && active.unread > 0) {
      api.post(`/messages/${encodeURIComponent(active.phone)}/mark-read`).catch(() => {});
    }
  }, [active, api]);

  // Auto-scroll the active thread to bottom whenever it changes / new msg arrives
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [activePhone, active?.messages?.length]);

  const filtered = threads.filter(t => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (t.name || '').toLowerCase().includes(s) || (t.phone || '').includes(s);
  });

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    setSending(true);
    setError('');
    try {
      const text = reply.trim();
      setReply('');
      // Optimistically append
      const tempId = `temp-${Date.now()}`;
      setThreads(prev => prev.map(t => t.phone === active.phone
        ? { ...t, messages: [...t.messages, { id: tempId, direction: 'outbound', body: text, status: 'sending', created_at: new Date().toISOString() }] }
        : t));
      const { data } = await api.post('/messages/reply', { to_phone: active.phone, message: text });
      if (!data.ok) {
        setError(data.error || 'Send failed.');
      }
      await fetchThreads();
    } catch (e) {
      setError(e.response?.data?.detail || 'Send failed.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="messaging-page" className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-gray-900 dark:text-white">Messaging</h1>
          <p className="text-gray-500 dark:text-slate-400 text-sm mt-0.5">Two-way SMS with leads and customers</p>
        </div>
      </div>

      {threads.length === 0 && !loading ? (
        <GlassCard className="text-center py-16">
          <MessageSquare className="w-10 h-10 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-slate-400 text-sm">No conversations yet. When Ava texts a lead or a customer replies, threads appear here.</p>
        </GlassCard>
      ) : (
        <div className="grid lg:grid-cols-[340px_1fr] gap-4 h-[calc(100vh-220px)] min-h-[480px]">
          {/* Thread list — full width on mobile, hidden when a thread is open */}
          <GlassCard className={`flex flex-col overflow-hidden p-0 ${active ? 'hidden lg:flex' : 'flex'}`}>
            <div className="p-4 border-b border-gray-100 dark:border-white/[0.06]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-slate-500" />
                <input
                  data-testid="messaging-search"
                  placeholder="Search conversations..." value={search} onChange={e => setSearch(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-full pl-9 pr-3 py-2 text-xs text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-blue-500" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-10 text-gray-400 dark:text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /></div>
              ) : filtered.map(t => {
                const last = t.messages?.[t.messages.length - 1];
                const isActive = t.phone === activePhone;
                return (
                  <button key={t.phone}
                    data-testid={`thread-${t.phone}`}
                    onClick={() => setActivePhone(t.phone)}
                    className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-gray-100 dark:border-white/[0.04] transition-colors ${isActive ? 'bg-blue-50 dark:bg-blue-500/10' : 'hover:bg-gray-50 dark:hover:bg-white/[0.03]'}`}>
                    <GradientAvatar name={t.name || t.phone} size={36} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{t.name || t.phone}</p>
                        <span className="text-[10px] text-gray-400 dark:text-slate-500 flex-shrink-0">{relTime(t.last_at)}</span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-slate-400 truncate">{last?.body || ''}</p>
                    </div>
                    {t.unread > 0 && (
                      <span className="flex-shrink-0 bg-blue-600 text-white text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                        {t.unread}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </GlassCard>

          {/* Active conversation — full width on mobile when picked, hidden by default on mobile */}
          <GlassCard className={`flex flex-col overflow-hidden p-0 ${active ? 'flex' : 'hidden lg:flex'}`}>
            {active ? (
              <>
                {/* Header */}
                <div className="flex items-center gap-3 px-4 sm:px-5 py-3.5 border-b border-gray-100 dark:border-white/[0.06]">
                  <button onClick={() => setActivePhone(null)} aria-label="Back to threads"
                    className="lg:hidden -ml-1 p-1.5 rounded-full text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-white/[0.05] flex-shrink-0">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="w-10 h-10 rounded-full bg-blue-500/15 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-semibold text-gray-900 dark:text-white truncate">{active.name || active.phone}</p>
                    <p className="text-[11px] text-gray-500 dark:text-slate-400">
                      {active.name ? <span className="font-mono tabular-nums">{active.phone}</span> : <span>{relTime(active.last_at)}</span>}
                    </p>
                  </div>
                </div>

                {/* Messages — iMessage style */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                  {active.messages?.map(m => (
                    <Bubble key={m.id} m={m} />
                  ))}
                  {/* Confirmation footer if last message is outbound 'sent' and there's an inbound reply after it */}
                  {hasConfirmedReply(active.messages) && (
                    <div className="flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-400 pt-1 pl-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Confirmed · added to calendar
                    </div>
                  )}
                </div>

                {/* Composer */}
                <div className="border-t border-gray-100 dark:border-white/[0.06] p-3">
                  {error && <p className="text-xs text-red-600 dark:text-red-400 mb-2 px-2">{error}</p>}
                  <div className="flex items-end gap-2">
                    <textarea
                      data-testid="reply-input"
                      value={reply}
                      onChange={e => setReply(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                      placeholder="Type a reply..."
                      rows={1}
                      className="flex-1 resize-none bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-2xl px-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-blue-500 max-h-32"
                    />
                    <button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="reply-send-btn"
                      aria-label="Send reply"
                      className="bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-slate-500 text-sm">
                Pick a conversation to view messages
              </div>
            )}
          </GlassCard>
        </div>
      )}
    </div>
  );
}

function Bubble({ m }) {
  const isOut = m.direction === 'outbound';
  const isAva = isOut && m.sender === 'ava';
  return (
    <div className={`flex flex-col ${isOut ? 'items-end' : 'items-start'}`}>
      {isOut && (
        <span className={`text-[10px] mb-0.5 uppercase tracking-[0.14em] font-semibold ${isAva ? 'text-purple-500' : 'text-blue-600 dark:text-blue-400'}`}>
          {isAva ? '🤖 Ava' : '👤 You'}
        </span>
      )}
      <div
        className={`max-w-[78%] px-3.5 py-2 rounded-2xl text-sm leading-snug ${
          isOut
            ? (isAva
                ? 'bg-gradient-to-br from-purple-600 to-blue-600 text-white rounded-br-md'
                : 'bg-blue-600 text-white rounded-br-md')
            : 'bg-gray-100 dark:bg-white/[0.06] text-gray-900 dark:text-slate-100 rounded-bl-md'
        }`}
      >
        {m.body}
        {isOut && m.status === 'failed' && (
          <span className="block text-[10px] text-red-200 mt-1">Failed to send</span>
        )}
        {isOut && m.status === 'sending' && (
          <span className="block text-[10px] text-blue-100 mt-1">Sending...</span>
        )}
      </div>
    </div>
  );
}

function hasConfirmedReply(msgs) {
  if (!msgs || msgs.length < 2) return false;
  const last = msgs[msgs.length - 1];
  // If the last message is inbound (customer replied), show "Confirmed"
  return last && last.direction === 'inbound';
}

function relTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d`;
    return d.toLocaleDateString();
  } catch { return ''; }
}

// Keep Check import alive in case future status indicators need it
// eslint-disable-next-line no-unused-vars
const _unused = Check;
